/**
 * ドメイン詳細 `/domains/$domainId`。
 * タブ: 概要 / DNS / DKIM / メールボックス / 証明書
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calculator,
  Download,
  Eraser,
  Globe,
  KeyRound,
  Lock,
  Mail,
  MoreHorizontal,
  Network,
  RefreshCw,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CopyableCode,
  CursorPagination,
  DataTable,
  DnsRecordTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  KeyValue,
  PageHeader,
  Section,
  StatusBadge,
  UsageBar,
  formatBytes,
  formatCount,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
  type DnsRecordCheck,
} from '@mail-sv/ui';
import { labelOf, type ExpectedDnsRecord } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { daysUntil } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { ConfirmTextDialog } from '../components/dialogs.js';
import { DetailSkeleton, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type { DomainDetail, DomainDnsReport, MailboxSummary } from '../types/api.js';

const routeApi = getRouteApi(RID.domainDetail);

export function DomainDetailPage(): React.ReactElement {
  const { domainId } = routeApi.useParams();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  const query = useQuery({
    queryKey: qk.domain(domainId),
    queryFn: () => api.get<DomainDetail>(`/domains/${domainId}`),
  });

  const domain = query.data;

  return (
    <>
      <PageHeader
        title={domain?.name ?? 'ドメイン'}
        description={domain ? `契約組織: ${domain.organizationName}` : undefined}
        breadcrumbs={[{ label: 'ドメイン', href: '/domains' }, { label: domain?.name ?? '…' }]}
        actions={
          <>
            <RefetchingDot active={query.isFetching && !query.isLoading} />
            {domain ? <StatusBadge status={domain.status} dot /> : null}
          </>
        }
      />

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.isLoading ? <DetailSkeleton /> : null}

      {domain ? (
        <>
          {!domain.outboundEnabled ? (
            <Alert variant="warning" className="mb-5">
              <AlertTitle>このドメインからの送信は停止しています</AlertTitle>
              <AlertDescription>
                所有権が未確認、または契約組織が停止中です。受信は継続しています。
              </AlertDescription>
            </Alert>
          ) : null}

          <PageTabs
            value={search.tab}
            onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
            tabs={[
              { value: 'overview', label: '概要', icon: Globe },
              { value: 'dns', label: 'DNS', icon: Network },
              { value: 'dkim', label: 'DKIM', icon: KeyRound },
              { value: 'mailboxes', label: 'メールボックス', icon: Mail },
              { value: 'cert', label: '証明書', icon: Lock },
            ]}
          />

          {search.tab === 'overview' ? <OverviewTab domain={domain} /> : null}
          {search.tab === 'dns' ? <DnsTab domainId={domain.id} /> : null}
          {search.tab === 'dkim' ? <DkimTab domain={domain} /> : null}
          {search.tab === 'mailboxes' ? <MailboxesTab domain={domain} /> : null}
          {search.tab === 'cert' ? <CertTab domain={domain} /> : null}
        </>
      ) : null}
    </>
  );
}

/* ---------------------------------------------------------------- overview */

function OverviewTab({ domain }: { domain: DomainDetail }): React.ReactElement {
  return (
    <Section title="基本情報">
      <div className="space-y-4 rounded-lg border border-border bg-card p-4">
        <KeyValue
          columns={3}
          items={[
            { key: 'ドメイン', value: domain.name },
            {
              key: '契約組織',
              value: (
                <Link
                  to="/organizations/$orgId"
                  params={{ orgId: domain.organizationId }}
                  search={{ tab: 'overview' }}
                  className="hover:text-primary"
                >
                  {domain.organizationName}
                </Link>
              ),
            },
            { key: '状態', value: <StatusBadge status={domain.status} size="sm" /> },
            {
              key: '収容ノード',
              value: domain.nodeId ? (
                <Link
                  to="/nodes/$nodeId"
                  params={{ nodeId: domain.nodeId }}
                  search={{ tab: 'overview', range: '24h' }}
                  className="hover:text-primary"
                >
                  {domain.nodeHostname ?? domain.nodeId}
                </Link>
              ) : (
                '—'
              ),
            },
            { key: 'メールボックス', value: formatCount(domain.mailboxCount) },
            { key: '送信', value: domain.outboundEnabled ? '許可' : '停止中' },
            { key: 'キャッチオール', value: domain.catchAllAddress ?? '未設定' },
            { key: 'DNS 最終確認', value: formatRelative(domain.dnsCheckedAt) },
            { key: '追加日', value: formatDateTime(domain.createdAt) },
          ]}
        />
        <CopyableCode label="所有権確認トークン" value={domain.verificationToken} />
      </div>
    </Section>
  );
}

/* --------------------------------------------------------------------- DNS */

function DnsTab({ domainId }: { domainId: string }): React.ReactElement {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: qk.domainDns(domainId),
    queryFn: () => api.get<DomainDnsReport>(`/domains/${domainId}/dns-records`),
    // 検証は明示操作でだけ走らせる。自動ポーリングすると外部 DNS を無駄に叩く。
    refetchInterval: false,
  });

  const verify = useMutation({
    mutationFn: () => api.post(`/domains/${domainId}/verify`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.domainDns(domainId) });
      void qc.invalidateQueries({ queryKey: qk.domain(domainId) });
    },
  });

  const report = query.data;

  // 期待値と実測値を `@mail-sv/ui` の <DnsRecordTable> の 2 引数へ分解する。
  const records: ExpectedDnsRecord[] = React.useMemo(
    () =>
      (report?.records ?? []).map((r) => ({
        kind: r.kind,
        name: r.name,
        type: r.type as ExpectedDnsRecord['type'],
        priority: r.priority,
        value: r.expected,
        ttl: r.ttl ?? 3600,
        required: r.required,
        description: r.description,
      })),
    [report],
  );

  const results: DnsRecordCheck[] = React.useMemo(
    () =>
      (report?.records ?? []).map((r) => ({
        kind: r.kind,
        name: r.name,
        result: r.result,
        observed: r.observed,
        checkedAt: report?.checkedAt ?? null,
      })),
    [report],
  );

  return (
    <Section
      title="DNS レコード"
      description="ここに出る期待値は検証ジョブが照合している値と同一（同じ関数から生成しています）。"
      actions={
        <Button variant="outline" size="sm" onClick={() => verify.mutate()} loading={verify.isPending}>
          <RefreshCw aria-hidden />
          今すぐ再チェック
        </Button>
      }
    >
      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      {report ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <StatusBadge
              status={report.overall === 'verified' ? 'ok' : report.overall === 'partial' ? 'mismatch' : 'error'}
              label={
                report.overall === 'verified' ? 'すべて設定済み' : report.overall === 'partial' ? '一部未設定' : '未設定'
              }
              dot
            />
            <span className="text-xs text-muted-foreground">最終確認 {formatDateTime(report.checkedAt)}</span>
          </div>
          <DnsRecordTable records={records} results={results} />
        </div>
      ) : null}
    </Section>
  );
}

/* -------------------------------------------------------------------- DKIM */

function DkimTab({ domain }: { domain: DomainDetail }): React.ReactElement {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.nodeWrite);

  const rotate = useMutation({
    mutationFn: () => api.post(`/dkim/keys/rotate`, { domainId: domain.id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.domain(domain.id) }),
  });

  return (
    <Section
      title="DKIM 鍵"
      description="ローテートは新旧を併存させ、DNS への反映を確認してから旧鍵を撤去します。撤去まで署名検証は途切れません。"
      actions={
        canWrite ? (
          <Button variant="outline" size="sm" onClick={() => rotate.mutate()} loading={rotate.isPending}>
            <RefreshCw aria-hidden />
            鍵をローテート
          </Button>
        ) : null
      }
    >
      {domain.dkimKeys.length === 0 ? (
        <p className="text-sm text-muted-foreground">DKIM 鍵がまだ生成されていません。</p>
      ) : (
        <div className="space-y-3">
          {domain.dkimKeys.map((k) => (
            <div key={k.id} className="space-y-3 rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <code className="font-mono text-sm font-medium">{k.selector}</code>
                <Badge variant="secondary" size="sm">
                  {labelOf(k.algorithm)}
                </Badge>
                <StatusBadge status={k.state} size="sm" />
                <span className="text-xs text-muted-foreground">生成 {formatDateTime(k.createdAt)}</span>
                {k.retiredAt ? (
                  <span className="text-xs text-warning">撤去予定 {formatDateTime(k.retiredAt)}</span>
                ) : null}
              </div>
              <CopyableCode
                label={`${k.selector}._domainkey.${domain.name} の TXT 値`}
                value={`v=DKIM1; k=${k.algorithm.startsWith('rsa') ? 'rsa' : 'ed25519'}; p=${k.publicKeyBase64}`}
              />
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        秘密鍵はこの画面には出しません（<code className="font-mono">ops.secret.read</code> + 再認証 + 監査が必要な専用経路でのみ取得できます）。
      </p>
    </Section>
  );
}

/* --------------------------------------------------------------- mailboxes */

function MailboxesTab({ domain }: { domain: DomainDetail }): React.ReactElement {
  const domainId = domain.id;
  const canCommand = usePermission(OPS_PERMISSION.nodeCommand);
  const [purging, setPurging] = React.useState<MailboxSummary | null>(null);

  const list = useCursorList<MailboxSummary>({
    path: `/domains/${domainId}/mailboxes`,
    keyFor: (params) => qk.domainMailboxes(domainId, params),
    filters: {},
  });

  const columns: Array<DataTableColumn<MailboxSummary>> = [
    { id: 'address', header: 'メールアドレス', cell: (m) => <span className="font-medium">{m.address}</span> },
    { id: 'status', header: '状態', cell: (m) => <StatusBadge status={m.status} size="sm" /> },
    {
      id: 'quota',
      header: '容量',
      width: 'w-44',
      cell: (m) => <UsageBar used={m.usedBytes} total={m.quotaBytes} formatValue={(v) => formatBytes(v)} />,
    },
    { id: 'lastLogin', header: '最終ログイン', hideBelow: 'md', cell: (m) => formatRelative(m.lastLoginAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-12',
      // ノードが割り当たっていないドメインへコマンドは撃てない（宛先が無い）。
      cell: (m) =>
        canCommand && domain.nodeId ? (
          <MailboxCommandMenu
            nodeId={domain.nodeId}
            mailbox={m}
            onPurge={() => setPurging(m)}
          />
        ) : null,
    },
  ];

  return (
    <>
      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}
      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(m) => m.id}
        loading={list.query.isLoading}
        emptyTitle="メールボックスがありません"
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <PurgeMailboxDialog
        nodeId={domain.nodeId}
        domainName={domain.name}
        mailbox={purging}
        onClose={() => setPurging(null)}
      />
    </>
  );
}

/* ------------------------------------------ メールボックスのノード操作（R75） */

/**
 * `mailbox.recalc-quota` / `mailbox.purge` の発行口。
 *
 * どちらも汎用の `POST /nodes/:id/commands` で撃てるが、**管理コンソールから
 * 実行する導線がどこにも無かった**（node-detail の「コマンド送信」は引数なしの
 * ものだけを並べている）。クォータのずれと Maildir の残骸は、契約者から
 * 「容量が空かない」と言われて初めて気付く類いの不整合なので、
 * メールボックスの一覧から直接撃てるようにする。
 */
function MailboxCommandMenu({
  nodeId,
  mailbox,
  onPurge,
}: {
  nodeId: string;
  mailbox: MailboxSummary;
  onPurge: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const recalc = useMutation({
    mutationFn: () =>
      api.post(`/nodes/${nodeId}/commands`, {
        kind: 'mailbox.recalc-quota',
        // **キーは `args`**（`payload` ではない）。サーバー側は `.strict()`。
        args: { address: mailbox.address },
        dedupeKey: `mailbox-recalc-quota-${mailbox.id}`,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.nodeCommands(nodeId) }),
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="メールボックスの操作">
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onSelect={() => recalc.mutate()} disabled={recalc.isPending}>
          <Calculator className="size-4" aria-hidden />
          クォータを再計算
        </DropdownMenuItem>
        <DropdownMenuItem className="text-destructive" onSelect={onPurge}>
          <Eraser className="size-4" aria-hidden />
          Maildir を完全消去
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Maildir の完全消去。**取り返しがつかない**ので、アドレスの完全一致入力を要求する。
 * 論理削除済みのメールボックスの残骸を片付けるための操作で、
 * 稼働中のメールボックスに撃つと契約者のメールが全部消える。
 */
function PurgeMailboxDialog({
  nodeId,
  domainName,
  mailbox,
  onClose,
}: {
  nodeId: string | null;
  domainName: string;
  mailbox: MailboxSummary | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const purge = useMutation({
    mutationFn: () =>
      api.post(`/nodes/${nodeId ?? ''}/commands`, {
        kind: 'mailbox.purge',
        args: { domain: domainName, localPart: mailbox?.localPart ?? '' },
        dedupeKey: `mailbox-purge-${mailbox?.id ?? ''}`,
      }),
    onSuccess: () => {
      if (nodeId) void qc.invalidateQueries({ queryKey: qk.nodeCommands(nodeId) });
      onClose();
    },
  });

  return (
    <ConfirmTextDialog
      open={mailbox !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Maildir を完全消去する"
      description="ノード上のメール実体を削除します。バックアップからの復元以外に戻す手段はありません。"
      confirmText={mailbox?.address ?? ''}
      confirmLabel="完全に消去する"
      pending={purge.isPending}
      error={purge.error}
      onConfirm={() => purge.mutate()}
    >
      <Alert variant="destructive">
        <AlertDescription>
          稼働中のメールボックスに実行すると、受信済みのメールがすべて失われます。
          <strong>解約後の残骸を片付ける場合</strong>にのみ使用してください。
        </AlertDescription>
      </Alert>
    </ConfirmTextDialog>
  );
}

/* --------------------------------------------------------------------- 証明書 */

function CertTab({ domain }: { domain: DomainDetail }): React.ReactElement {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.nodeWrite);
  const cert = domain.certificate;

  const canCommand = usePermission(OPS_PERMISSION.nodeCommand);

  const renew = useMutation({
    mutationFn: () => api.post(`/certificates/${cert?.id ?? ''}/renew`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.domain(domain.id) }),
  });

  /*
   * **再インストールは「更新」とは別の操作**（R75）。
   * ACME は成功しているのにノード上の PEM が古い（配置に失敗した・
   * 手で置き換えられた）ケースは、更新を何度押しても直らない。
   * `cert.install` は CP が持っている証明書をノードへ配り直すだけ。
   */
  const install = useMutation({
    mutationFn: () =>
      api.post(`/nodes/${domain.nodeId ?? ''}/commands`, {
        kind: 'cert.install',
        args: { certId: cert?.id ?? '' },
        dedupeKey: `cert-install-${cert?.id ?? ''}`,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.domain(domain.id) }),
  });

  if (!cert) {
    return (
      <Section title="証明書">
        <Alert variant="warning">
          <AlertTitle>証明書が未発行です</AlertTitle>
          <AlertDescription>
            DNS の所有権確認が完了すると ACME キューが自動で発行します。手動発行が必要な場合は運用手順を参照してください。
          </AlertDescription>
        </Alert>
      </Section>
    );
  }

  const days = daysUntil(cert.notAfter);

  return (
    <Section
      title="証明書"
      actions={
        <>
          {canWrite ? (
            <Button variant="outline" size="sm" onClick={() => renew.mutate()} loading={renew.isPending}>
              <RefreshCw aria-hidden />
              今すぐ更新
            </Button>
          ) : null}
          {canCommand && domain.nodeId ? (
            <Button variant="outline" size="sm" onClick={() => install.mutate()} loading={install.isPending}>
              <Download aria-hidden />
              ノードへ再インストール
            </Button>
          ) : null}
        </>
      }
    >
      <div className="rounded-lg border border-border bg-card p-4">
        <KeyValue
          columns={2}
          items={[
            { key: '状態', value: <StatusBadge status={cert.status} size="sm" dot /> },
            { key: '発行者', value: cert.issuer ?? '—' },
            { key: '有効期間', value: `${formatDateTime(cert.notBefore)} 〜 ${formatDateTime(cert.notAfter)}` },
            {
              key: '残日数',
              value:
                days === null ? (
                  '—'
                ) : (
                  <span className={days <= 7 ? 'text-destructive' : days <= 30 ? 'text-warning' : undefined}>
                    {days} 日
                  </span>
                ),
            },
            { key: '最終更新', value: formatDateTime(cert.renewedAt) },
            { key: 'SAN', value: cert.sans.length > 0 ? cert.sans.join(', ') : '—' },
          ]}
        />
      </div>
    </Section>
  );
}
